import { HTTPClient, HTTPMethod, HTTPResponse } from "./HTTPClient";
import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

type RetryableAxiosRequestConfig = InternalAxiosRequestConfig & {
  _retry?: boolean;
  skipAuthRefresh?: boolean;
};

export class AxiosHTTPClient extends HTTPClient {
  private client: AxiosInstance;
  private refreshPromise: Promise<boolean> | null;

  constructor() {
    super();
    this.client = axios.create({
      withCredentials: true
    });
    this.refreshPromise = null;
    this.setupInterceptors();
  }

  private fromAxiosErrToHttpResponse(error: AxiosError): HTTPResponse {
    if (error.response) {
      const detail = (error.response.data as { detail?: string | object } | undefined)?.detail;
      const detailMessage = typeof detail === "string" ? detail : detail ? ` - ${JSON.stringify(detail)}` : "";

      return {
        success: false,
        errorMsg: `${error.response.status} ${error.response.statusText}${detailMessage}`,
        data: {}
      };
    } else {
      return {
        success: false,
        errorMsg: error.message,
        data: {}
      };
    }
  }

  private setupInterceptors() {
    this.client.interceptors.request.use((config) => {
      config.withCredentials = true;
      return config;
    });

    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const requestConfig = error.config as RetryableAxiosRequestConfig | undefined;
        const requestUrl = requestConfig?.url || "";
        const isUnauthorized = error.response?.status === 401;
        const isAuthEndpoint =
          requestUrl.includes("/api/auth/login") ||
          requestUrl.includes("/api/auth/refresh") ||
          requestUrl.includes("/api/auth/logout");

        if (
          isUnauthorized &&
          requestConfig &&
          !requestConfig._retry &&
          !requestConfig.skipAuthRefresh &&
          !isAuthEndpoint
        ) {
          requestConfig._retry = true;
          const refreshOk = await this.refreshAccessToken();
          if (refreshOk) {
            return this.client.request(requestConfig);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.client
        .post("/api/auth/refresh", {}, { skipAuthRefresh: true } as RetryableAxiosRequestConfig)
        .then((response) => {
          const payload = response.data as { result?: boolean };
          return !!payload.result;
        })
        .catch(() => false)
        .finally(() => {
          this.refreshPromise = null;
        });
    }

    return this.refreshPromise;
  }

  public async send(method: HTTPMethod, url: string, params: object): Promise<HTTPResponse> {
    let httpResponse: HTTPResponse;
    try {
      let axiosResponse: AxiosResponse;
      switch (method) {
        case HTTPMethod.GET:
          axiosResponse = await this.client.get(url, { params });
          break;
        case HTTPMethod.POST:
          axiosResponse = await this.client.post(url, params);
          break;
      }
      httpResponse = {
        success: true,
        errorMsg: "",
        data: axiosResponse.data
      };
    } catch (error) {
      httpResponse = this.fromAxiosErrToHttpResponse(<AxiosError>error);
    }
    return Promise.resolve(httpResponse);
  }
}
